import React, { useEffect, useMemo, useState } from 'react';
import { equals, filter, map, prop, propEq, compose, findIndex, assocPath, assoc, flatten, reduce, length, update, forEach, pathEq, remove, propOr } from 'ramda';
import { isNotNilOrEmpty, propNotEq } from 'ramda-adjunct';
import InviteMemberMutation from '../_graphql/mutations/users/InviteMemberMutation';
import UpdateRolesMutation from '../_graphql/mutations/users/UpdateRolesMutation';
import { errorMessage, successMessage } from '../utils/messageMutation';
import { ROLES } from '../lib/rights';
import { getRolesToDisplayed, RoleActionEnum } from '../components/users/utils/roles';
import { emailIsValid as emailIsValidFn } from '../lib/validation';
import { buildRole, getSuperAdminRole, getUserRoles } from '../pages/administration/components/users/utils';
import useUserOfferOptions from './useUserOfferOptions';
import useUserTeamOptions from './useUserTeamOptions';
import useUserFormFilterOptions from './useUserFormFilterOptions';
import useEmailAlreadyUsed from './useEmailAlreadyUsed';
import useUserFormValidation from './useUserFormValidation';
import useUserRoleOptions from './useUserRoleOptions';
import useActionGuard from './useActionGuard';

const useUserForm = ({ open, handleClose, selectedUser, teams, offers, users, isAdministration, defaultTeamId, connectedUser }) => {
  const isEditMode = !!selectedUser;
  const [email, setEmail] = useState('');
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [selectedOffers, setSelectedOffers] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [rolesList, setRolesList] = useState(getUserRoles(selectedUser, teams, offers));
  const { loading, run } = useActionGuard();

  useEffect(() => setRolesList(getUserRoles(selectedUser, teams, offers)), [selectedUser, teams, offers]);

  const {
    emailAlreadyUsed,
    userExistIsSuperAdmin,
    isConnectedUserSelected,
    userIsTeamMember,
    displayUpdateUserMsg,
    canInviteExistingUser,
    existingUser,
    isPendingUser
  } = useEmailAlreadyUsed({
    email,
    users,
    connectedUserEmail: prop('email', connectedUser),
    defaultTeamId,
    isAdministration,
    isEditMode
  });

  const rolesToUpdate = useMemo(
    () => filter(({ action }) => isNotNilOrEmpty(action), rolesList),
    [rolesList]
  );

  const rolesDisplayed = useMemo(
    () => compose(
      getRolesToDisplayed,
      filter(propNotEq(RoleActionEnum.DELETE, 'action'))
    )(rolesList),
    [rolesList]
  );

  const { isSuperAdmin, disabledTeams, disabledOffers, currentTeamIsDisabled } = useUserFormFilterOptions(rolesDisplayed, selectedTeam);

  const emailIsValid = emailIsValidFn(email);

  const teamsOption = useUserTeamOptions({
    teams,
    disabledTeams
  });

  const {
    selectedOptionsValue,
    offersOption
  } = useUserOfferOptions({
    offers,
    selectedTeam,
    disabledTeams,
    disabledOffers,
    currentTeamIsDisabled
  });

  const rolesOption = useUserRoleOptions({
    selectedTeam,
    offersOption,
    rolesDisplayed
  });

  const resetUserForm = () => {
    setEmail('');
    isAdministration && setSelectedTeam('');
    setSelectedOffers([]);
    setSelectedRole('');
    setRolesList([]);
  };

  useEffect(() => {
    if (open) {
      return resetUserForm;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!isAdministration && isNotNilOrEmpty(defaultTeamId)) {
      setSelectedTeam(defaultTeamId);
    }
  }, [isAdministration, defaultTeamId]);

  useEffect(() => {
    if (isNotNilOrEmpty(selectedUser)) {
      setRolesList(getUserRoles(selectedUser, teams, offers));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUser]);

  useEffect(() => {
    if (isNotNilOrEmpty(selectedTeam) && isNotNilOrEmpty(selectedOffers)) {
      setSelectedOffers([]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTeam]);

  useEffect(() => {
    const adminRoleIsSelected = isNotNilOrEmpty(selectedRole) && equals(ROLES.admin, selectedRole);
    if (adminRoleIsSelected && isNotNilOrEmpty(selectedOffers)) {
      setSelectedOffers([]);
    }
  }, [selectedOffers, selectedRole]);

  const updateOrAddRole = (prevRoles, newRole) => {
    const existingIndex = findIndex(
      (role) =>
        propEq(prop('roleValue', newRole), 'roleValue', role) &&
        propEq(prop('teamId', newRole), 'teamId', role) &&
        propEq(prop('offerId', newRole), 'offerId', role),
      prevRoles
    );

    if (existingIndex !== -1) {
      return update(existingIndex, assoc('action', null, prevRoles[existingIndex]), prevRoles);
    }

    return [...prevRoles, { ...newRole, new: true }];
  };

  const onAdd = ({
    selectedTeam,
    selectedOffers,
    selectedRole
  }) => {
    const isAdmin = equals(ROLES.admin, selectedRole);
    const baseRole = {
      teamId: selectedTeam,
      roleValue: selectedRole,
      teams,
      offers
    };
    const newRoles = isAdmin ? [buildRole(baseRole, true)] : map((offerId) => buildRole({
      ...baseRole,
      offerId
    }, true), selectedOffers);

    setRolesList((prevRoles) => {
      if (isAdmin) {
        prevRoles = map((role) => {
          const toDelete = propEq(selectedTeam, 'teamId', role);
          return toDelete ? assoc('action', RoleActionEnum.DELETE, role) : role;
        }, prevRoles);
      }

      let updatedRoles = prevRoles;
      forEach((newRole) => {
        updatedRoles = updateOrAddRole(updatedRoles, newRole);
      }, newRoles);

      return updatedRoles;
    });

    isAdministration && setSelectedTeam(null);
    setSelectedOffers([]);
    setSelectedRole(null);
  };

  const onAddSuperAdmin = () => {
    setRolesList((prevRoles) => {
      const superAdminRole = getSuperAdminRole(true);
      const deletedPrev = map(assoc('action', RoleActionEnum.DELETE), prevRoles);
      return updateOrAddRole(deletedPrev, superAdminRole);
    });
  };
  const onDelete = (uuids) => setRolesList((prev) => reduce((updatedList, uuid) => {
    const roleIndex = findIndex(propEq(uuid, 'uuid'), updatedList);
    const isNewRole = pathEq(true, [roleIndex, 'new'], updatedList);
    if (roleIndex === -1) return updatedList;
    else if (isNewRole) return remove(roleIndex, 1, updatedList);
    return assocPath([roleIndex, 'action'], RoleActionEnum.DELETE, updatedList);
  }, prev)(uuids));

  const adminRoleIsSelected = equals(ROLES.admin, selectedRole);
  const isValid = isNotNilOrEmpty(selectedTeam) && isNotNilOrEmpty(selectedRole) && (isNotNilOrEmpty(selectedOffers) || adminRoleIsSelected);

  const inviteExistingUserTeam = !isAdministration && emailAlreadyUsed && canInviteExistingUser && !isPendingUser;
  const getConfirmActionKey = () => (
    isEditMode || inviteExistingUserTeam
      ? `update-roles-${propOr('pending','id', inviteExistingUserTeam ? existingUser : selectedUser)}`
      : `invite-member-${email}`
  );
    
  const onConfirm = () => {
    const handleResponse = (ok, error) => {
      if (ok && !error) {
        const action = isEditMode ? 'mis à jour' : 'ajouté';
        const isPlural = length(rolesToUpdate) > 1;
        successMessage('rôle', action, false, false, isPlural);
        handleClose(true);
      } else {
        errorMessage();
      }
    };

    const key = getConfirmActionKey();
    if (isEditMode || inviteExistingUserTeam) {
      const roles = compose(
        flatten,
        map(({ teamId, roleValue: role, action, offer, team: teamName }) => {
          const offersToUpdateRole = filter(({ offerName, team }) => equals(offerName, offer) && equals(teamId, team))(offers);
          if (equals(role, ROLES.superAdmin)) {
            return {
              role, action, teamName: 'Super Admin'
            };
          }
          if (equals(role, ROLES.admin)) {
            return {
              teamId, role, action, teamName
            };
          }
          return map(({ id: offerId }) => ({
            teamId, offerId, role, action, teamName
          }))(offersToUpdateRole);
        })
      )(rolesToUpdate);
      run(key, (done) => UpdateRolesMutation({
        userId: prop('id', inviteExistingUserTeam ? existingUser : selectedUser),
        roles
      }, done), handleResponse);
    } else {
      const rolesToAdd = map(({
        teamId, offerId, roleValue, team
      }) => ({
        teamId,
        offerId,
        role: roleValue,
        teamName: team
      }), rolesList);
      run(key, (done) => InviteMemberMutation({ email, roles: rolesToAdd }, done), handleResponse);
    }
  };

  const validations = useUserFormValidation({
    isEditMode,
    emailIsValid,
    isAdministration,
    selectedTeam,
    currentTeamIsDisabled,
    isSuperAdmin,
    emailAlreadyUsed,
    rolesDisplayed,
    rolesToUpdate,
    isConnectedUserSelected,
    userExistIsSuperAdmin,
    userIsTeamMember,
    selectedRole,
    adminRoleIsSelected,
    isValid,
    isPendingUser
  });
  
  return {
    isEditMode,
    onConfirm,
    onDelete,
    onAdd,
    onAddSuperAdmin,
    email,
    setEmail,
    selectedTeam,
    setSelectedTeam,
    selectedOffers,
    setSelectedOffers,
    selectedRole,
    setSelectedRole,
    emailAlreadyUsed,
    displayUpdateUserMsg,
    userExistIsSuperAdmin,
    emailIsValid,
    isSuperAdmin,
    adminRoleIsSelected,
    isValid,
    currentTeamIsDisabled,
    teamsOption,
    selectedOptionsValue,
    offersOption,
    rolesOption,
    rolesToUpdate,
    rolesDisplayed,
    resetUserForm,
    isConnectedUserSelected,
    userIsTeamMember,
    validations,
    isPendingUser,
    loading: propOr(false, getConfirmActionKey(), loading)
  };
};

export default useUserForm;